/** Global Imports */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  memo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Link,
  Tooltip,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

/** Component Imports */
import { styles } from './styles';
import GridViewIcon from 'images/grid-view.svg';
import ListViewIcon from 'images/list-view.svg';
import FilteredGrid from 'components/common/grid/filtered-grid';
import ImageStacker from 'components/common/image-stacker';
import RequestAccess from 'components/workflows/ProgramDashboard/request-program-access';
import ProgramModeSelection from '../program-mode-selection';
import ProgramFilters from '../program-filters';
import ProgramCurrentFilters from '../program-current-filters';
import { PrimaryButton, SecondaryButton } from 'components/common/buttons';
import AddProgram from '../add-program';
import ProgramCard from '../program-card';
import Pagination from '@material-ui/lab/Pagination';
import { makeStyles } from '@material-ui/styles';

/** Context Imports */
import { useLoader } from 'context/loader';
import { useNotification } from 'context/notification';
import { useBreadcrumb } from 'context/breadcrumb';
import { collaborationRoutes } from 'router/routes';
import { userContext } from 'context/user';

/** Service Imports */
import { getAllPrograms } from 'services/programs';
import { buSelector } from 'redux/business-units/business-units-selector';
import { ppSelector } from 'redux/product-platforms/product-platforms-selector';
import { setAllProgramsToStore } from 'redux/actions/program';

function Programs({ history }) {
  const classes = styles();

  const { updateBreadcrumb } = useBreadcrumb();
  const { dispatchLoader } = useLoader();
  const { dispatchNotification } = useNotification();
  const { currentUser } = useContext(userContext);
  const [programOwnerInfo, setProgramOwnerInfo] = useState('');

  const [allPrograms, setAllPrograms] = useState([]);
  const [activeMode, setActiveMode] = useState('my');
  const [programsViewStyle, setProgramsViewStyle] = useState('grid');
  const [isAddProgramPopupOpen, setAddProgramPopupOpen] = useState(false);
  const [isRequestAccessProgramPopupOpen, setRequestAccessProgramPopupOpen] =
    useState(false);
  const [programInfo, setProgramNameInfo] = useState('');

  const globalUsers = useSelector((state) => state.userReducer.globalUsers);
  const businessUnits = useSelector(buSelector);
  const productPlatforms = useSelector(ppSelector);
  const dispatch = useDispatch();

  const userId = useMemo(() => currentUser.userId, [currentUser]);

  const filters = useMemo(() => {
    const programName = {
      show: true,
      isCheckboxList: false,
      isSearchable: false,
    };
    const productPlatformName = {
      show: true,
      isCheckboxList: true,
      isSearchable: false,
      title: 'Product Platforms',
      masterList: productPlatforms,
    };
    const businessUnitName = {
      show: true,
      isCheckboxList: true,
      isSearchable: false,
      title: 'Business Units',
      masterList: businessUnits,
    };
    const ownerId = {
      show: activeMode === 'all',
      isCheckboxList: true,
      isSearchable: true,
      title: 'Owners',
      masterList: [
        ...globalUsers.map((gu) => ({
          id: gu.userID,
          name: `${gu.firstName} ${gu.lastName}`,
        })),
      ],
    };

    return {
      programName,
      productPlatformName,
      businessUnitName,
      ownerId,
    };
  }, [productPlatforms, businessUnits, globalUsers, activeMode]);
  const [activeFilters, setActiveFilters] = useState({});

  const filteredPrograms = useMemo(() => {
    let programsAfterFilter = [...allPrograms];
    Object.keys(activeFilters).forEach((filterName) => {
      if (activeFilters[filterName] && activeFilters[filterName].length > 0) {
        switch (filterName) {
          case 'programName':
            programsAfterFilter = [
              ...programsAfterFilter.filter((program) =>
                activeFilters[filterName].some((pp) =>
                  program.programName
                    .toLowerCase()
                    .includes(pp.name.toLowerCase()),
                ),
              ),
            ];
            break;
          case 'productPlatformName':
            programsAfterFilter = [
              ...programsAfterFilter.filter((program) =>
                activeFilters[filterName].some(
                  (pp) => pp.name === program.productPlatformName,
                ),
              ),
            ];
            break;
          case 'businessUnitName':
            programsAfterFilter = [
              ...programsAfterFilter.filter((program) =>
                activeFilters[filterName].some(
                  (bu) => bu.name === program.businessUnitName,
                ),
              ),
            ];
            break;
          case 'ownerId':
            if (activeMode == 'my') {
              programsAfterFilter = [
                ...programsAfterFilter.filter((program) =>
                  activeFilters[filterName].some(
                    (owner) => owner.id === userId,
                  ),
                ),
              ];
            } else {
              programsAfterFilter = [
                ...programsAfterFilter.filter((program) =>
                  activeFilters[filterName].some(
                    (owner) => owner.id === program.ownerId,
                  ),
                ),
              ];
            }
            break;
        }
      }
    });

    return programsAfterFilter;
  }, [activeFilters, allPrograms]);

  const updateProgramsBreadcrumb = useCallback(() => {
    updateBreadcrumb({
      key: collaborationRoutes.programs.key,
      name: 'Dashboard',
      path: collaborationRoutes.programs.path,
    });
  }, [collaborationRoutes.programs.key, collaborationRoutes.programs.path]);

  const navigateToProgramDetails = useCallback(
    ({ programId }) => {
      history.push(collaborationRoutes.program.getPath(programId));
    },
    [history, collaborationRoutes],
  );

  const updateAllPrograms = useCallback(() => {
    dispatchLoader({ show: true, message: 'Loading the programs..' });
    getAllPrograms(activeFilters).then((data) => {
      if (data.status === 'success') {
        dispatch(setAllProgramsToStore(data.programs));
        setAllPrograms(data.programs);
        dispatchLoader({ show: false });
      } else {
        dispatchLoader({ show: false });
        dispatchNotification({
          show: true,
          severity: 'error',
          message: data.error_message,
        });
      }
    });
  }, [activeFilters]);

  useEffect(() => {
    updateAllPrograms();
  }, [activeFilters]);

  const handleAddProgramClick = useCallback((e) => {
    setAddProgramPopupOpen(true);
  }, []);

  const handleCloseAddProgramPopup = useCallback(() => {
    setAddProgramPopupOpen(false);
  }, []);

  const handleProgramViewStyleChange = useCallback((viewStyle) => {
    setProgramsViewStyle(viewStyle);
  }, []);

  const handleRequestAccessButtonClick = useCallback(
    (event, programInfo, ownerInfo) => {
      event.stopPropagation();
      event.preventDefault();
      setProgramOwnerInfo(ownerInfo);
      setProgramNameInfo(programInfo);
      setRequestAccessProgramPopupOpen(true);
    },
    [],
  );

  const handleCloseRequestAccessPopup = useCallback(() => {
    setRequestAccessProgramPopupOpen(false);
  }, []);

  const RenderProgramsInGridStyle = memo(({ filteredPrograms }) => {
    return filteredPrograms.map((program, index) => (
      <ProgramCard
        key={`${program.name}-${index}`}
        programInfo={program}
        userId={userId}
        navigateToProgramDetails={navigateToProgramDetails}
        activeMode={activeMode}
        handleRequestAccessButtonClick={handleRequestAccessButtonClick}
      />
    ));
  });

  const useStyles = makeStyles({
    root: {
      display: 'flex',
      margin: 'auto',
    },
  });

  const RenderProgramsInGridStylePaging = memo(({ filteredPrograms }) => {
    const [pageSize, setPageSize] = useState(9);
    const [currentPage, setCurrentPage] = useState(1);
    const lastCardIndex = currentPage * pageSize;
    const firstCardIndex = lastCardIndex - pageSize;
    const dataToDisplay = filteredPrograms.slice(firstCardIndex, lastCardIndex);
    const [numberOfPages, setNumberOfPages] = useState(
      Math.ceil(filteredPrograms.length / pageSize),
    );
    const handlePageRangeChange = (value) => {
      setPageSize(value);
      setNumberOfPages(Math.ceil(filteredPrograms.length / value));
      if (currentPage > Math.ceil(filteredPrograms.length / value)) {
        setCurrentPage(Math.ceil(filteredPrograms.length / value));
      }
    };

    return (
      <>
        <div className={classes.programsGridView}>
          <RenderProgramsInGridStyle filteredPrograms={dataToDisplay} />
        </div>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            margin: '1em auto',
            justifyContent: 'space-between',
          }}
        >
          <Box width={'12%'}>
            <FormControl fullWidth>
              <InputLabel>Page size</InputLabel>
              <Select
                value={pageSize}
                label="Page Size"
                onChange={(e) => handlePageRangeChange(e.target.value)}
              >
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value={15}>15</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            className={classes.paginationContainer}
            color="primary"
            count={numberOfPages}
            page={currentPage}
            showFirstButton={true}
            showLastButton={true}
            siblingCount={3}
            boundaryCount={2}
            onChange={(event, currentPage) => setCurrentPage(currentPage)}
          />
        </Box>
      </>
    );
  });

  const RenderProgramsInListStyle = memo(({ filteredPrograms }) => {
    const gridColumns = useMemo(
      () => [
        {
          name: 'Program Name',
          width: 225,
        },
        {
          name: 'Product Platform',
          width: 200,
        },
        {
          name: 'Business Unit',
          width: 200,
        },
        {
          name: 'Owner',
          width: 250,
        },
        {
          name: 'Request Access',
          headerLabel: '',
          width: 150,
        },
        {
          name: 'Team',
          width: 125,
        },
      ],
      [],
    );

    const gridRows = useMemo(() => {
      return filteredPrograms
        .filter((program) =>
          activeMode === 'my' ? program.users.includes(userId) : program,
        )
        .map((program, index) => {
          let ownerInfo = globalUsers.find((x) => x.userID == program.ownerId);

          let programUsers = program.users
            .map((user) => {
              let userDetails = globalUsers.find((x) => x.userID == user);
              if (userDetails !== undefined) {
                return {
                  id: user,
                  userId: user,
                  firstName: userDetails.firstName,
                  lastName: userDetails.lastName,
                  url: '',
                };
              } else {
                return {};
              }
            })
            .filter(
              (user) => user !== undefined && Object.entries(user).length > 0,
            );

          return {
            id: index + 1,
            programId: program.programId,
            programName: program.programName,
            'Program Name': program.users.includes(currentUser.userId) ? (
              <Link
                onClick={() =>
                  navigateToProgramDetails({ programId: program.programId })
                }
                to=""
                className={classes.programNameLink}
                actualvalue={program.programName}
                sortcomparatorkey={'programName'}
              >
                {program.programName}
              </Link>
            ) : (
              program.programName
            ),
            productPlatformId: program.productPlatformId,
            'Product Platform': (
              <label
                style={{
                  color: productPlatforms
                    .find((pp) => pp.id === program.productPlatformId)
                    .colorCode.replace(', 0.1', ''),
                }}
              >
                {' '}
                {program.productPlatformName}
              </label>
            ),
            businessUnitId: program.businessUnitId,
            'Business Unit': program.businessUnitName,
            ownerId: program.ownerId,
            Owner: ownerInfo
              ? `${ownerInfo.firstName} ${ownerInfo.lastName}`
              : '',
            Team: (
              <div className={classes.programUsers}>
                <ImageStacker
                  imageList={programUsers}
                  maxImagesToDisplay={4}
                  overridenStyles={classes.userImage}
                  bgColor="#1891F6"
                />
              </div>
            ),
            requestAccess: '',
            'Request Access': program.users.includes(
              currentUser.userId,
            ) ? null : program.requesters.includes(currentUser.userId) ? (
              <SecondaryButton
                classes={{ root: classes.btnPendingAccess }}
                variant="outlined"
                startIcon={<LockOutlinedIcon />}
              >
                Pending
              </SecondaryButton>
            ) : (
              <SecondaryButton
                classes={{ root: classes.btnRequestAccess }}
                variant="outlined"
                onClick={(e) =>
                  handleRequestAccessButtonClick(e, program, ownerInfo)
                }
              >
                Request access
              </SecondaryButton>
            ),
          };
        });
    }, [filteredPrograms, globalUsers]);

    return (
      <FilteredGrid
        columns={gridColumns}
        rows={gridRows}
        sortable={false}
        headerHeight={40}
      />
    );
  });

  useEffect(() => {
    updateProgramsBreadcrumb();
    updateAllPrograms();
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.modeSelection}>
        <ProgramModeSelection activeMode={activeMode} setMode={setActiveMode} />
      </div>
      <div className={classes.programsMain}>
        <div className={classes.filterSection}>
          {businessUnits !== undefined && (
            <ProgramFilters
              filters={filters}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
            />
          )}
        </div>
        <div className={classes.programsSection}>
          <div className={classes.programsSectionTopBar}>
            <ProgramCurrentFilters
              filters={filters}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
            />
            <span className={classes.programOptions}>
              {programsViewStyle === 'grid' ? (
                <Tooltip title="Switch to List-View">
                  <img
                    src={ListViewIcon}
                    alt="List View"
                    className={classes.imgProgramListViewMode}
                    onClick={() => handleProgramViewStyleChange('list')}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Switch to Grid-View">
                  <img
                    src={GridViewIcon}
                    alt="Grid View"
                    className={classes.imgProgramListViewMode}
                    onClick={() => handleProgramViewStyleChange('grid')}
                  />
                </Tooltip>
              )}
              {activeMode === 'my' && (
                <PrimaryButton
                  classes={{ root: classes.btnAddProgram }}
                  onClick={handleAddProgramClick}
                  startIcon={<AddIcon />}
                >
                  New Program
                </PrimaryButton>
              )}
            </span>
          </div>
          <div className={classes.programsGridViewList}>
            {programsViewStyle === 'grid' && (
              <RenderProgramsInGridStylePaging
                filteredPrograms={filteredPrograms.filter((program) =>
                  activeMode === 'my'
                    ? program.users.includes(userId)
                    : program,
                )}
              />
            )}
          </div>
          <div className={classes.programListView}>
            {programsViewStyle === 'list' && (
              <RenderProgramsInListStyle filteredPrograms={filteredPrograms} />
            )}
          </div>
        </div>
      </div>
      <>
        <AddProgram
          isOpen={isAddProgramPopupOpen}
          closePopup={handleCloseAddProgramPopup}
          productPlatforms={productPlatforms}
          businessUnits={businessUnits}
          allPrograms={allPrograms}
          navigateToProgramDetails={navigateToProgramDetails}
        />
        <RequestAccess
          programInfo={programInfo}
          isOpen={isRequestAccessProgramPopupOpen}
          closePopup={handleCloseRequestAccessPopup}
          currentUser={currentUser}
          programOwnerInfo={programOwnerInfo}
          updateAllPrograms={updateAllPrograms}
        />
      </>
    </div>
  );
}

export default withRouter(Programs);
